import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../PageHeader/PageHeader';
import { HitApi } from '../../../Api/ApiHit';
import { addProductInWarehouse, searchProduct, addStock } from '../../../constant/Constant';
import { getSelectedUnit } from '../../../storage/Storage';
import toast from 'react-hot-toast';

function AddWarehouse() {
    const navigate = useNavigate();
    
    // Get URL parameters for pre-selection
    const urlParams = new URLSearchParams(window.location.search);
    const urlLocation = urlParams.get('location');
    const urlType = urlParams.get('type');
    
    const [formData, setFormData] = useState({
        productId: '',
        quantity: '',
        costPrice: '',
        sellPrice: '',
        mrp: '',
        vendor: '',
        batchNumber: '',
        expiryDate: '',
        type: urlType || 'professional', // professional or retail - use URL param if available
        place: 'warehouse', // warehouse, shelves, inStock
        stockLocation: urlLocation || 'salon', // warehouse or salon - default to salon unless coming from warehouse
        stockAlert: ''
    });
    
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [productList, setProductList] = useState([]);
    const [editingIndex, setEditingIndex] = useState(-1);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [activeFilter, setActiveFilter] = useState(urlType || 'all'); // Current filter state

    // Fetch initial products for selection (basic list)
    useEffect(() => {
        fetchInitialProducts();
    }, [activeFilter]); // Re-fetch when filter changes

    // Close dropdown when clicking outside and cleanup
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.relative')) {
                setShowDropdown(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            // Cleanup search timeout on unmount
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    const fetchInitialProducts = useCallback(async () => {
        setIsLoadingProducts(true);
        try {
            const searchObj = {
                unitIds: getSelectedUnit()?._id
            };

            // Add type filter based on active filter
            if (activeFilter && activeFilter !== 'all') {
                // Map filter type to actual vendor values in database
                const vendorMap = {
                    'professional': 'Professional',
                    'retail': 'Retail'
                };
                const vendorValue = vendorMap[activeFilter];
                if (vendorValue) {
                    searchObj.vendor = vendorValue;
                }
            }
            // If activeFilter is 'all' or not specified, don't add vendor filter to show all products

            const json = {
                page: 1,
                limit: 50, // Reduced limit for initial load
                search: searchObj
            };

            const res = await HitApi(json, searchProduct);
            
            if (res?.statusCode === 200) {
                const productsData = res?.data?.docs || res?.products || res?.result || [];
                setProducts(productsData);
                setFilteredProducts(productsData);
            } else {
                toast.error('Failed to load products');
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error('Error loading products');
        } finally {
            setIsLoadingProducts(false);
        }
    }, [activeFilter]); // Add activeFilter as dependency

    const searchProductsFromAPI = async (searchTerm) => {
        setIsSearching(true);
        try {
            const searchObj = {
                unitIds: getSelectedUnit()?._id,
                $or: [
                    { productName: { $regex: searchTerm, $options: 'i' } },
                    { brand: { $regex: searchTerm, $options: 'i' } },
                    { productType: { $regex: searchTerm, $options: 'i' } }
                ]
            };

            // Add type filter based on active filter
            if (activeFilter && activeFilter !== 'all') {
                // Map filter type to actual vendor values in database
                const vendorMap = {
                    'professional': 'Professional',
                    'retail': 'Retail'
                };
                const vendorValue = vendorMap[activeFilter];
                if (vendorValue) {
                    searchObj.vendor = vendorValue;
                }
            }
            // If activeFilter is 'all' or not specified, don't add vendor filter to show all products

            const json = {
                page: 1,
                limit: 100,
                search: searchObj
            };

            const res = await HitApi(json, searchProduct);
            
            if (res?.statusCode === 200) {
                const productsData = res?.data?.docs || res?.products || res?.result || [];
                setFilteredProducts(productsData);
            } else {
                setFilteredProducts([]);
            }
        } catch (error) {
            console.error('Error searching products:', error);
            setFilteredProducts([]);
        } finally {
            setIsSearching(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleProductSelect = (product) => {
        setSelectedProduct(product);
        
        // Determine usage type from vendor field
        const usageType = product?.vendor?.toLowerCase() === 'professional' ? 'professional' : 'retail';
        
        setFormData(prev => ({
            ...prev,
            productId: product._id,
            costPrice: product?.costPrice || '',
            sellPrice: product?.sellPrice || '',
            mrp: product?.mrp || '',
            type: usageType, // Auto-fill usage type based on vendor
            vendor: product?.vendor || ''
        }));
        setSearchTerm(`${product.productName} - ${product.brand || 'No Brand'}`);
        setShowDropdown(false);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        setShowDropdown(true);
        
        // Clear existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        
        if (value.trim() === '') {
            setFilteredProducts(products); // Show initial products
            setSelectedProduct(null);
            setFormData(prev => ({ ...prev, productId: '' }));
            return;
        }

        // Always use API search for live search with minimal debounce
        const timeout = setTimeout(() => {
            searchProductsFromAPI(value.trim());
        }, 150); // Reduced debounce to 150ms for faster response
        setSearchTimeout(timeout);
    };

    // Handle filter change
    const handleFilterChange = (newFilter) => {
        setActiveFilter(newFilter);
        
        // Clear search when filter changes
        setSearchTerm('');
        setSelectedProduct(null);
        setShowDropdown(false);
        
        // Update form data type based on new filter
        const newType = newFilter === 'all' ? 'professional' : newFilter; // Default to professional for 'all'
        setFormData(prev => ({ 
            ...prev, 
            productId: '',
            type: newType
        }));
        
        // Clear any existing search timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
    };

    const addToList = () => {
        if (!formData.productId) {
            toast.error('Please select a product');
            return;
        }
        
        if (!formData.quantity || formData.quantity <= 0) {
            toast.error('Please enter a valid quantity');
            return;
        }

        // Check if product already exists in list
        const existingIndex = productList.findIndex(item => item.productId === formData.productId);
        
        const newItem = {
            ...formData,
            productName: selectedProduct?.productName || 'Unknown Product',
            brand: selectedProduct?.brand || '',
            quantity: parseInt(formData.quantity),
            costPrice: parseFloat(formData.costPrice) || 0,
            sellPrice: parseFloat(formData.sellPrice) || 0,
            mrp: parseFloat(formData.mrp) || 0,
            stockAlert: parseInt(formData.stockAlert) || 0,
            id: Date.now() // temporary ID for list management
        };

        if (editingIndex >= 0) {
            // Update existing item
            const updatedList = [...productList];
            updatedList[editingIndex] = newItem;
            setProductList(updatedList);
            setEditingIndex(-1);
            toast.success('Product updated in list');
        } else if (existingIndex >= 0) {
            // Update quantity if product already exists
            const updatedList = [...productList];
            updatedList[existingIndex].quantity += newItem.quantity;
            setProductList(updatedList);
            toast.success('Product quantity updated');
        } else {
            // Add new item
            setProductList(prev => [...prev, newItem]);
            toast.success('Product added to list');
        }

        // Reset form
        setFormData({
            productId: '',
            quantity: '',
            costPrice: '',
            sellPrice: '',
            mrp: '',
            vendor: '',
            batchNumber: '',
            expiryDate: '',
            type: urlType || 'professional',
            place: 'warehouse',
            stockLocation: urlLocation || 'salon',
            stockAlert: ''
        });
        setSelectedProduct(null);
        setSearchTerm('');
        setShowDropdown(false);
    };

    const editProduct = (index) => {
        const product = productList[index];
        const originalProduct = products.find(p => p._id === product.productId);
        
        setFormData({
            productId: product.productId,
            quantity: product.quantity.toString(),
            costPrice: product.costPrice.toString(),
            sellPrice: product.sellPrice.toString(),
            mrp: product.mrp.toString(),
            vendor: product.vendor,
            batchNumber: product.batchNumber,
            expiryDate: product.expiryDate,
            type: product.type || 'professional',
            place: product.place || 'warehouse',
            stockLocation: product.stockLocation || 'salon',
            stockAlert: product.stockAlert?.toString() || ''
        });
        setSelectedProduct(originalProduct);
        setSearchTerm(`${originalProduct?.productName || ''} - ${originalProduct?.brand || 'No Brand'}`);
        setEditingIndex(index);
    };

    const removeProduct = (index) => {
        setProductList(prev => prev.filter((_, i) => i !== index));
        toast.success('Product removed from list');
        if (editingIndex === index) {
            setEditingIndex(-1);
            setFormData({
                productId: '',
                quantity: '',
                costPrice: '',
                sellPrice: '',
                mrp: '',
                vendor: '',
                batchNumber: '',
                expiryDate: '',
                type: urlType || 'professional',
                place: 'warehouse',
                stockLocation: urlLocation || 'salon',
                stockAlert: ''
            });
            setSelectedProduct(null);
            setSearchTerm('');
            setShowDropdown(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (productList.length === 0) {
            toast.error('Please add at least one product to the list');
            return;
        }

        setIsLoading(true);
        
        try {
            let successCount = 0;
            let errorCount = 0;

            for (const product of productList) {
                try {
                    // First add to warehouse (legacy endpoint)
                    const warehouseData = {
                        productId: product.productId,
                        quantity: product.quantity,
                        costPrice: product.costPrice,
                        sellPrice: product.sellPrice,
                        mrp: product.mrp,
                        vendor: product.vendor,
                        batchNumber: product.batchNumber,
                        expiryDate: product.expiryDate,
                        unitIds: getSelectedUnit()?._id
                    };

                    const warehouseRes = await HitApi(warehouseData, addProductInWarehouse);
                    
                    // Then create stock entry (new Stock schema)
                    const stockData = {
                        warehouseId: getSelectedUnit()?._id, // Using unit as warehouse for now
                        productId: product.productId,
                        unitId: getSelectedUnit()?._id,
                        type: product.type || 'professional',
                        place: product.place || 'warehouse',
                        stockLocation: product.stockLocation || 'salon',
                        qty: product.quantity, // Add qty field
                        stockIn: product.quantity,
                        stockAlert: product.stockAlert || 0,
                        stockOut: 0,
                        isActive: true
                    };

                    const stockRes = await HitApi(stockData, addStock);
                    
                    if ((warehouseRes?.success || warehouseRes?.statusCode === 200 || warehouseRes?.message?.includes('success')) &&
                        (stockRes?.success || stockRes?.statusCode === 200 || stockRes?.message?.includes('success'))) {
                        successCount++;
                    } else {
                        errorCount++;
                        console.error(`Failed to add ${product.productName}:`, 
                            warehouseRes?.message || stockRes?.message || 'Unknown error');
                    }
                } catch (error) {
                    errorCount++;
                    console.error(`Error adding ${product.productName}:`, error);
                }
            }

            if (successCount > 0) {
                toast.success(`${successCount} products added successfully!`);
                if (errorCount === 0) {
                    // Navigate back to appropriate page based on stock location
                    const returnPath = urlLocation === 'salon' ? '/stock/professionals' : '/stock/warehouse';
                    navigate(returnPath);
                } else {
                    toast.error(`${errorCount} products failed to add`);
                }
            } else {
                toast.error('Failed to add any products');
            }
        } catch (error) {
            console.error('Error adding products to warehouse:', error);
            toast.error('Failed to add products');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        // Navigate back to appropriate page based on stock location
        const returnPath = urlLocation === 'salon' ? '/stock/professionals' : '/stock/warehouse';
        navigate(returnPath);
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (!amount) return '₹0.00';
        return `₹${parseFloat(amount).toFixed(2)}`;
    };

    return (
        <div className="p-5">
            <PageHeader
                title={urlLocation === 'salon' ? "Add Products to Salon" : "Add Products to Warehouse"}
                description={urlLocation === 'salon' ? 
                    `Add single or multiple ${urlType || 'professional'} products to salon inventory` : 
                    "Add single or multiple products to warehouse inventory"
                }
            />

            {/* Product Selection Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {editingIndex >= 0 ? 'Edit Product' : 'Add Product to List'}
                </h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                    {/* Product Selection */}
                    <div className="lg:col-span-2 relative">
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Search Product *
                            </label>
                            
                            {/* Product Type Filter */}
                            <div className="flex space-x-1">
                                {[
                                    { key: 'all', label: 'All', color: 'gray' },
                                    { key: 'professional', label: 'Professional', color: 'purple' },
                                    { key: 'retail', label: 'Retail', color: 'orange' }
                                ].map((filter) => {
                                    // Determine if button should be enabled based on entry point
                                    const isEnabled = () => {
                                        // Check type first (more specific)
                                        
                                        // From professionals: only professional button enabled
                                        if (urlType === 'professional') {
                                            return filter.key === 'professional';
                                        }
                                        
                                        // From retails: only retail button enabled
                                        if (urlType === 'retail') {
                                            return filter.key === 'retail';
                                        }
                                        
                                        // From warehouse with 'all' type: all buttons enabled
                                        if (urlType === 'all' || !urlType) {
                                            return true;
                                        }
                                        
                                        // Default: enable all
                                        return true;
                                    };
                                    
                                    const enabled = isEnabled();
                                    
                                    return (
                                        <button
                                            key={filter.key}
                                            onClick={() => enabled && handleFilterChange(filter.key)}
                                            disabled={!enabled}
                                            className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 ${
                                                !enabled 
                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                                                    : activeFilter === filter.key
                                                        ? filter.key === 'all' 
                                                            ? 'bg-gray-600 text-white'
                                                            : filter.key === 'professional'
                                                            ? 'bg-purple-600 text-white'
                                                            : 'bg-orange-600 text-white'
                                                        : filter.key === 'all'
                                                            ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                            : filter.key === 'professional'
                                                            ? 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                                                            : 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                                            }`}
                                            title={
                                                !enabled 
                                                    ? `${filter.label} filter not available from this section`
                                                    : `Filter to show only ${filter.key === 'all' ? 'all products' : filter.key + ' products'}`
                                            }
                                        >
                                            {filter.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onFocus={() => setShowDropdown(true)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder={
                                isLoadingProducts ? 'Loading products...' :
                                isSearching ? 'Searching...' :
                                'Type to search products (live search)...'
                            }
                            disabled={isLoadingProducts}
                        />
                        
                        {/* Filter Status */}
                        <div className="mt-1 text-xs text-gray-500">
                            {activeFilter === 'all' ? 'Searching all product types' :
                             activeFilter === 'professional' ? 'Searching professional products only' :
                             'Searching retail products only'}
                        </div>
                        
                        {/* Dropdown */}
                        {showDropdown && !isLoadingProducts && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                {isSearching ? (
                                    <div className="p-3 text-center text-gray-500">
                                        <div className="flex items-center justify-center space-x-2">
                                            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                                            <span>Searching products...</span>
                                        </div>
                                    </div>
                                ) : filteredProducts.length > 0 ? (
                                    filteredProducts.map(product => (
                                        <div
                                            key={product._id}
                                            onClick={() => handleProductSelect(product)}
                                            className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                        >
                                            <div className="font-medium text-gray-900">
                                                {product.productName}
                                            </div>
                                            <div className="text-sm text-gray-500 flex justify-between items-center">
                                                <div className="flex items-center space-x-2">
                                                    <span>{product.brand || 'No Brand'}</span>
                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                        product.vendor?.toLowerCase() === 'professional' 
                                                            ? 'bg-purple-100 text-purple-800' 
                                                            : 'bg-orange-100 text-orange-800'
                                                    }`}>
                                                        {product.vendor || 'N/A'}
                                                    </span>
                                                </div>
                                                <span className="text-green-600 font-medium">₹{product.sellPrice || 0}</span>
                                            </div>
                                        </div>
                                    ))
                                ) : searchTerm.trim() !== '' ? (
                                    <div className="p-3 text-center text-gray-500">
                                        No products found for "{searchTerm}"
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>

                    {/* Quantity */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Quantity *
                        </label>
                        <input
                            type="number"
                            name="quantity"
                            value={formData.quantity}
                            onChange={handleInputChange}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter quantity"
                            min="1"
                            required
                        />
                    </div>

                    {/* Add Button */}
                    <div className="flex items-end">
                        <button
                            type="button"
                            onClick={addToList}
                            className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                            disabled={isLoadingProducts || !formData.productId || !formData.quantity}
                        >
                            {editingIndex >= 0 ? 'Update' : 'Add to List'}
                        </button>
                    </div>
                </div>

                {/* Selected Product Info */}
                {selectedProduct && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                        <h4 className="font-medium text-gray-900 mb-2">Product Details</h4>
                        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 text-sm text-gray-600">
                            <div>Brand: {selectedProduct.brand || 'N/A'}</div>
                            <div>Type: {selectedProduct.productType || 'N/A'}</div>
                            <div>
                                Usage: <span className={`font-medium ${
                                    selectedProduct.vendor?.toLowerCase() === 'professional' 
                                        ? 'text-purple-600' 
                                        : 'text-orange-600'
                                }`}>
                                    {selectedProduct.vendor || 'N/A'}
                                </span>
                            </div>
                            <div>Current Price: ₹{selectedProduct.sellPrice || 0}</div>
                            <div>MRP: ₹{selectedProduct.mrp || 0}</div>
                        </div>
                        
                        {/* Expandable Details */}
                        <details className="mt-3">
                            <summary className="cursor-pointer text-blue-600 hover:text-blue-800 text-sm">
                                Edit Pricing & Details
                            </summary>
                            <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-4">
                                {/* Price Fields */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Cost Price (₹)
                                    </label>
                                    <input
                                        type="number"
                                        name="costPrice"
                                        value={formData.costPrice}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Sell Price (₹)
                                    </label>
                                    <input
                                        type="number"
                                        name="sellPrice"
                                        value={formData.sellPrice}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        MRP (₹)
                                    </label>
                                    <input
                                        type="number"
                                        name="mrp"
                                        value={formData.mrp}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Vendor
                                    </label>
                                    <input
                                        type="text"
                                        name="vendor"
                                        value={formData.vendor}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Enter vendor name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Batch Number
                                    </label>
                                    <input
                                        type="text"
                                        name="batchNumber"
                                        value={formData.batchNumber}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Enter batch number"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Expiry Date
                                    </label>
                                    <input
                                        type="date"
                                        name="expiryDate"
                                        value={formData.expiryDate}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Stock Type
                                    </label>
                                    <select
                                        name="type"
                                        value={formData.type}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="professional">Professional</option>
                                        <option value="retail">Retail</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Place
                                    </label>
                                    <select
                                        name="place"
                                        value={formData.place}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="warehouse">Warehouse</option>
                                        <option value="shelves">Shelves</option>
                                        <option value="inStock">In Stock</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Stock Location *
                                    </label>
                                    <select
                                        name="stockLocation"
                                        value={formData.stockLocation}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    >
                                        <option value="warehouse">Warehouse</option>
                                        <option value="salon">Salon</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Stock Alert Limit
                                    </label>
                                    <input
                                        type="number"
                                        name="stockAlert"
                                        value={formData.stockAlert}
                                        onChange={handleInputChange}
                                        className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Minimum stock level"
                                        min="0"
                                    />
                                </div>
                            </div>
                        </details>
                    </div>
                )}
            </div>

            {/* Products List Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900">
                            Products List ({productList.length})
                        </h3>
                        {productList.length > 0 && (
                            <div className="text-sm text-gray-600">
                                Total Items: {productList.reduce((sum, item) => sum + item.quantity, 0)}
                            </div>
                        )}
                    </div>

                    {productList.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <div className="text-4xl mb-4">📦</div>
                            <p>No products added yet</p>
                            <p className="text-sm">Add products using the form to see them here</p>
                        </div>
                    ) : (
                        <div className="max-h-96 overflow-y-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr className="border-b">
                                        <th className="text-left py-2 px-3 font-medium">Product</th>
                                        <th className="text-center py-2 px-2 font-medium">Qty</th>
                                        <th className="text-right py-2 px-2 font-medium">Unit Cost</th>
                                        <th className="text-right py-2 px-2 font-medium">Total Cost</th>
                                        <th className="text-right py-2 px-2 font-medium">Unit Sell</th>
                                        <th className="text-right py-2 px-2 font-medium">Total Sell</th>
                                        <th className="text-center py-2 px-2 font-medium">Type</th>
                                        <th className="text-center py-2 px-2 font-medium">Location</th>
                                        <th className="text-center py-2 px-3 font-medium">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {productList.map((product, index) => (
                                        <tr 
                                            key={product.id} 
                                            className={`border-b hover:bg-gray-50 ${editingIndex === index ? 'bg-blue-50' : ''}`}
                                        >
                                            <td className="py-2 px-3">
                                                <div className="font-medium text-gray-900">
                                                    {product.productName}
                                                </div>
                                                {product.brand && (
                                                    <div className="text-xs text-gray-500">
                                                        {product.brand}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-2 px-2 text-center">
                                                <span className="font-medium">{product.quantity}</span>
                                            </td>
                                            <td className="py-2 px-2 text-right">
                                                <span className="text-sm text-blue-600">
                                                    {formatCurrency(product.costPrice)}
                                                </span>
                                            </td>
                                            <td className="py-2 px-2 text-right">
                                                <span className="font-medium text-blue-700">
                                                    {formatCurrency(product.costPrice * product.quantity)}
                                                </span>
                                            </td>
                                            <td className="py-2 px-2 text-right">
                                                <span className="text-sm text-green-600">
                                                    {formatCurrency(product.sellPrice)}
                                                </span>
                                            </td>
                                            <td className="py-2 px-2 text-right">
                                                <span className="font-medium text-green-700">
                                                    {formatCurrency(product.sellPrice * product.quantity)}
                                                </span>
                                            </td>
                                            <td className="py-2 px-2 text-center">
                                                <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                                    product.type === 'professional' 
                                                        ? 'bg-purple-100 text-purple-800' 
                                                        : 'bg-orange-100 text-orange-800'
                                                }`}>
                                                    {product.type === 'professional' ? 'Professional' : 'Retail'}
                                                </span>
                                            </td>
                                            <td className="py-2 px-2 text-center">
                                                <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                                    product.stockLocation === 'warehouse' 
                                                        ? 'bg-blue-100 text-blue-800' 
                                                        : 'bg-green-100 text-green-800'
                                                }`}>
                                                    {product.stockLocation === 'warehouse' ? 'Warehouse' : 'Salon'}
                                                </span>
                                            </td>
                                            <td className="py-2 px-3 text-center">
                                                <div className="flex justify-center space-x-2">
                                                    <button
                                                        onClick={() => editProduct(index)}
                                                        className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 rounded hover:bg-blue-100"
                                                        disabled={isLoading}
                                                        title="Edit product"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => removeProduct(index)}
                                                        className="text-red-600 hover:text-red-800 text-xs px-2 py-1 rounded hover:bg-red-100"
                                                        disabled={isLoading}
                                                        title="Remove product"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                                    <tr>
                                        <td className="py-3 px-3 font-semibold text-gray-900">
                                            Total ({productList.length} Products)
                                        </td>
                                        <td className="py-3 px-2 text-center font-semibold text-gray-900">
                                            {productList.reduce((sum, item) => sum + item.quantity, 0)}
                                        </td>
                                        <td className="py-3 px-2 text-right text-sm text-gray-600">
                                            Avg: {productList.length > 0 ? formatCurrency(productList.reduce((sum, item) => sum + item.costPrice, 0) / productList.length) : '₹0.00'}
                                        </td>
                                        <td className="py-3 px-2 text-right font-semibold text-blue-700">
                                            {formatCurrency(productList.reduce((sum, item) => sum + (item.costPrice * item.quantity), 0))}
                                        </td>
                                        <td className="py-3 px-2 text-right text-sm text-gray-600">
                                            Avg: {productList.length > 0 ? formatCurrency(productList.reduce((sum, item) => sum + item.sellPrice, 0) / productList.length) : '₹0.00'}
                                        </td>
                                        <td className="py-3 px-2 text-right font-semibold text-green-700">
                                            {formatCurrency(productList.reduce((sum, item) => sum + (item.sellPrice * item.quantity), 0))}
                                        </td>
                                        <td className="py-3 px-2"></td>
                                        <td className="py-3 px-2"></td>
                                        <td className="py-3 px-3"></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}

                    {/* Final Action Buttons */}
                    {productList.length > 0 && (
                        <div className="flex justify-between space-x-4 pt-6 border-t border-gray-200 mt-6">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Adding Products...' : 
                                    `Add ${productList.length} Products to ${urlLocation === 'salon' ? 'Salon' : 'Warehouse'}`}
                            </button>
                        </div>
                    )}
                </div>
        </div>
    );
}

export default AddWarehouse;